/**
 * Linha única de posologia do receituário estruturado.
 *
 * O receituário guarda dose, frequência, via e duração em colunas separadas —
 * é o que a impressão e os modelos consomem. Na tela, porém, o médico escreve
 * uma frase só ("Tomar 1 comprimido, de 12 em 12 horas, via oral, durante 7
 * dias"). Estas funções fazem a ponte nos dois sentidos.
 *
 * @see components/clinic/MedicationPosologyLineField.tsx
 */

export const ROUTE_OPTIONS = [
  'Oral', 'Sublingual', 'Tópica', 'Intramuscular', 'Intravenosa', 'Subcutânea',
  'Retal', 'Nasal', 'Inalatória', 'Oftálmica', 'Otológica', 'Vaginal',
];

export interface PosologyLineFields {
  dosage: string;
  frequency: string;
  route: string;
  duration: string;
}

/** Zera posologia ao trocar o medicamento e força resync do campo único. */
export function clearPosologyForNewMedication<
  T extends PosologyLineFields & { _posologyRev?: number },
>(item: T): T {
  return {
    ...item,
    dosage: '',
    frequency: '',
    duration: '',
    _posologyRev: (item._posologyRev || 0) + 1,
  };
}

interface PosologyLineSource {
  dosage?: string | null;
  frequency?: string | null;
  route?: string | null;
  duration?: string | null;
  pharmaceutical_form?: string | null;
}

const text = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim();

const deaccent = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** Verbos de administração já presentes no texto vindo da CMED. */
const ADMINISTRATION_VERB =
  /^(tomar|aplicar|usar|utilizar|instilar|inalar|administrar|ingerir|injetar|borrifar|pingar|introduzir|diluir|consultar)\b/i;

const VERB_BY_ROUTE: Record<string, string> = {
  topica: 'Aplicar',
  oftalmica: 'Instilar',
  otologica: 'Instilar',
  nasal: 'Instilar',
  inalatoria: 'Inalar',
  intramuscular: 'Administrar',
  intravenosa: 'Administrar',
  subcutanea: 'Administrar',
  vaginal: 'Introduzir',
  retal: 'Introduzir',
};

/**
 * Dose padrão por forma farmacêutica. Só cobre formas em que a unidade de
 * administração é a própria dose usual — nada aqui substitui decisão clínica.
 */
const DEFAULT_DOSAGE_BY_FORM: Record<string, string> = {
  comprimido: '1 comprimido',
  capsula: '1 cápsula',
  dragea: '1 drágea',
  supositorio: '1 supositório',
  sache: '1 sachê',
  pomada: 'fina camada',
  creme: 'fina camada',
  gel: 'fina camada',
};

const FREQUENCY_PATTERNS: RegExp[] = [
  /,?\s*de\s+\d+\s*(?:em|\/)\s*\d+\s*(?:horas?|hs?|dias?|semanas?|minutos?|min)\b/i,
  /,?\s*a\s+cada\s+\d+\s*(?:horas?|hs?|dias?|semanas?|minutos?|min)\b/i,
  /,?\s*\d+\s*\/\s*\d+\s*(?:h|horas?|d|dias?)\b/i,
  /,?\s*\d+\s*(?:x|vezes?)\s*(?:\/|\s*(?:ao|por|na|no)\s+)?\s*(?:dia|semana|m[êe]s|hora)s?\b/i,
  /,?\s*(?:uma|duas|tr[êe]s|quatro|seis)\s+vez(?:es)?\s+(?:ao|por)\s+(?:dia|semana)\b/i,
  /,?\s*dose\s+[úu]nica\b/i,
  /,?\s*individualizada\b/i,
  /,?\s*se\s+necess[áa]rio\b/i,
  /,?\s*(?:ao\s+deitar|pela\s+manh[ãa]|[àa]\s+noite|em\s+jejum)\b/i,
];

/** "8/8h" e "2x/dia" viram texto de receita: "de 8 em 8 horas", "2 vezes ao dia". */
export function humanizeFrequency(value: unknown): string {
  const raw = text(value);
  if (!raw) return '';

  const interval = raw.match(/^(\d+)\s*\/\s*(\d+)\s*h(?:oras?)?$/i);
  if (interval && interval[1] === interval[2]) return `de ${interval[1]} em ${interval[1]} horas`;

  const perDay = raw.match(/^(\d+)\s*x\s*\/?\s*(?:ao\s+)?dia$/i);
  if (perDay) return `${perDay[1]} ${perDay[1] === '1' ? 'vez' : 'vezes'} ao dia`;

  const range = raw.match(/^(\d+)\s*-\s*(\d+)\s*x\s*\/?\s*(?:ao\s+)?dia$/i);
  if (range) return `${range[1]} a ${range[2]} vezes ao dia`;

  return raw;
}

/** Unidades de administração — distinguem dose de concentração do rótulo. */
const ADMINISTRATION_UNIT =
  /\b(comprimido|cp|c[aá]psula|caps|dr[aá]gea|sach[eê]|suposit[oó]rio|gotas?|colher|ampola|aplica[cç][aã]o|camada|puff|jato|unidade|ui)\b/i;

/** Só número + unidade de peso/volume, sem unidade de administração (ex.: "100 mg"). */
const CONCENTRATION_ONLY =
  /^\d+([.,]\d+)?\s*(mg|g|mcg|µg|ui|u|ml|m?l|%)(?:\s*\/\s*(g|ml|m?l|dose|comprimido|c[aá]psula|cp))?$/i;

const normalizeAmount = (value: string): string =>
  deaccent(value).replace(/\s+/g, '').replace(/,/g, '.');

function looksLikeConcentrationNotDose(
  dosage: string,
  concentration?: string | null,
): boolean {
  const raw = text(dosage);
  if (!raw) return false;
  if (ADMINISTRATION_UNIT.test(raw)) return false;
  if (CONCENTRATION_ONLY.test(raw)) return true;
  if (concentration && normalizeAmount(raw) === normalizeAmount(concentration)) return true;
  return false;
}

/** Prefixa o verbo de administração quando a dose vem só com a unidade. */
export function humanizeDosage(value: unknown, route?: string | null): string {
  const raw = text(value);
  if (!raw || ADMINISTRATION_VERB.test(raw)) return raw;
  const verb = VERB_BY_ROUTE[deaccent(text(route))] || 'Tomar';
  return `${verb} ${raw.charAt(0).toLowerCase()}${raw.slice(1)}`;
}

export function humanizeDuration(value: unknown): string {
  const raw = text(value);
  if (!raw) return '';
  if (/^(durante|por)\b/i.test(raw)) return raw;
  if (/\d/.test(raw)) return `durante ${raw}`;
  if (/^curt[ao]$/i.test(raw)) return 'por curto período';
  return `por ${raw}`;
}

/** Dose sugerida quando a CMED não trouxe posologia verificada. */
export function defaultDosageForForm(form: unknown, route?: string | null): string {
  const unit = DEFAULT_DOSAGE_BY_FORM[deaccent(text(form))];
  return unit ? humanizeDosage(unit, route) : '';
}

/**
 * Converte a dose da CMED para texto de receita. Quando a curadoria trouxe só a
 * concentração ("100 mg"), usa a unidade de administração da forma ("1 comprimido").
 */
export function resolvePrescriptionDosage(params: {
  dosage?: string | null;
  pharmaceutical_form?: string | null;
  route?: string | null;
  concentration?: string | null;
}): string {
  const raw = text(params.dosage);
  if (!raw || /consultar bula/i.test(raw)) {
    return defaultDosageForForm(params.pharmaceutical_form, params.route);
  }
  if (looksLikeConcentrationNotDose(raw, params.concentration)) {
    const byForm = defaultDosageForForm(params.pharmaceutical_form, params.route);
    if (byForm) return byForm;
  }
  return humanizeDosage(raw, params.route);
}

/** Monta a frase exibida no campo único. */
export function composePosologyLine(item: PosologyLineSource): string {
  const dosage = text(item.dosage);
  const frequency = text(item.frequency);
  const duration = text(item.duration);
  const route = text(item.route);

  // Sem nada preenchido, a via padrão sozinha ("via oral") só polui a linha.
  if (!dosage && !frequency && !duration) return '';

  const parts = [dosage, frequency];
  if (route) parts.push(`via ${route.toLowerCase()}`);
  parts.push(humanizeDuration(duration));
  return parts.filter(Boolean).join(', ');
}

/**
 * Reconstrói os campos a partir da frase digitada. O que não for reconhecido
 * como frequência, via ou duração permanece na dose — assim nada do que o
 * médico escreveu se perde na impressão.
 */
export function parsePosologyLine(line: string): PosologyLineFields {
  let rest = text(line);
  let route = '';
  let frequency = '';
  let duration = '';

  const routeMatch = rest.match(/,?\s*(?:por\s+)?via\s+([a-zà-ú]+)/i);
  if (routeMatch) {
    const found = ROUTE_OPTIONS.find((option) => deaccent(option) === deaccent(routeMatch[1]));
    if (found) {
      route = found;
      rest = rest.replace(routeMatch[0], ' ');
    }
  }

  const durationMatch = rest.match(/,?\s*(?:durante|por)\s+([^,]+)/i);
  if (durationMatch) {
    duration = text(durationMatch[1]);
    rest = rest.replace(durationMatch[0], ' ');
  }

  for (const pattern of FREQUENCY_PATTERNS) {
    const match = rest.match(pattern);
    if (match) {
      frequency = text(match[0].replace(/^,/, ''));
      rest = rest.replace(match[0], ' ');
      break;
    }
  }

  const dosage = text(rest.replace(/,/g, ' '));
  return { dosage, frequency, route, duration };
}

/** Resumo curto usado em listagens e pré-visualizações. */
export function posologySummary(item: PosologyLineSource): string {
  return composePosologyLine(item);
}

/** Texto de apoio do campo, explicando de onde veio o preenchimento. */
export function posologyLineHelper(hint?: any): string {
  if (!hint) return 'Escolha o medicamento na lista CMED para preencher automaticamente.';
  const top = hint.suggestions?.[0];
  if (hint.source === 'verified_bula') {
    return `Bula profissional vigente da ANVISA — registro ${top?.verification?.registration || 'verificado'}.`;
  }
  if (hint.source === 'cmed_draft') {
    return hint.notice || 'Posologia CMED curada (rascunho) — revisar antes de prescrever.';
  }
  if (hint.source === 'professional_history' || hint.source === 'unit_history') {
    return `Posologia habitual${top?.times_used ? ` (usada ${top.times_used}x)` : ''}.`;
  }
  return hint.notice || 'Sem posologia verificada para esta apresentação — preencha manualmente.';
}
